import React from 'react';
import { KeyAccountsPortfolio } from '../api';
import MetricStat from './MetricStat';
import DeltaIndicator, { revenueDeltaText, cmDeltaText, snapshotDeltaText } from './DeltaIndicator';
import { formatCompactCurrency, formatPercent, formatDays } from './format';

interface Props {
    portfolio: KeyAccountsPortfolio;
}

// every tile after the first gets a vertical divider when the row fits on one line
const dividerClass = 'lg:border-l lg:border-gray-200 lg:pl-4';

const PortfolioTotalsStrip: React.FC<Props> = ({ portfolio }) => {
    return (
        <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Portfolio Totals</h2>
            <div className="bg-white border border-gray-200 rounded-lg shadow-md shadow-gray-200/60 px-5 py-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-4 gap-y-4">
                    <MetricStat
                        label="Revenue (PY)"
                        value={formatCompactCurrency(portfolio.revenue_py)}
                    />
                    <div className={dividerClass}>
                        <MetricStat
                            label="Revenue (CY)"
                            value={formatCompactCurrency(portfolio.revenue_cy)}
                            delta={
                                <DeltaIndicator
                                    delta={portfolio.revenue_growth_amt}
                                    text={revenueDeltaText(portfolio.revenue_growth_amt, portfolio.revenue_growth_pct)}
                                />
                            }
                        />
                    </div>
                    <div className={dividerClass}>
                        <MetricStat
                            label="CM"
                            value={formatCompactCurrency(portfolio.cm_cy)}
                            delta={
                                <DeltaIndicator
                                    delta={portfolio.cm_growth_amt}
                                    text={revenueDeltaText(portfolio.cm_growth_amt, portfolio.cm_growth_pct)}
                                />
                            }
                        />
                    </div>
                    <div className={dividerClass}>
                        <MetricStat
                            label="CM %"
                            value={formatPercent(portfolio.cm_pct_cy)}
                            delta={
                                <DeltaIndicator
                                    delta={portfolio.cm_pct_delta}
                                    text={portfolio.cm_pct_delta !== null ? cmDeltaText(portfolio.cm_pct_delta) : ''}
                                />
                            }
                        />
                    </div>
                    <div className={dividerClass}>
                        <MetricStat
                            label="Overdue >30"
                            value={formatCompactCurrency(portfolio.overdue_gt_30)}
                            valueClassName={portfolio.overdue_gt_30 > 0 ? 'text-red-700' : ''}
                            delta={
                                <DeltaIndicator
                                    delta={portfolio.overdue_gt_30_delta}
                                    text={portfolio.overdue_gt_30_delta !== null ? snapshotDeltaText(portfolio.overdue_gt_30_delta) : ''}
                                    invert
                                    nullText="— vs last run"
                                />
                            }
                        />
                    </div>
                    <div className={dividerClass}>
                        <MetricStat
                            label="Unbilled excl. stock"
                            value={formatCompactCurrency(portfolio.unbilled_excl_stock)}
                            delta={
                                <DeltaIndicator
                                    delta={portfolio.unbilled_excl_stock_delta}
                                    text={portfolio.unbilled_excl_stock_delta !== null ? snapshotDeltaText(portfolio.unbilled_excl_stock_delta) : ''}
                                    invert
                                    nullText="— vs last run"
                                />
                            }
                        />
                    </div>
                    <div className={dividerClass}>
                        <MetricStat label="DSO (Days)" value={formatDays(portfolio.dso)} />
                    </div>
                    <div className={dividerClass}>
                        <MetricStat
                            label="Total Exposure"
                            value={formatCompactCurrency(portfolio.total_exposure)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PortfolioTotalsStrip;
